import React, { useEffect, useState } from "react";
import { DataTableComponent } from "../../../Components/DataTable";
import Icon from "../../../Components/icons";
import { Chip, EmptyScreen } from "../../../Components/Other";
import { PrimaryButton } from "../../../Components/Cta";
import { APIService } from "../../../Service";
import { useNavigate } from "react-router-dom";
import Loader from "../../../Components/Other/loader";
import { toast } from "react-toastify";

export default function Transactions({ id }) {
  const navigate = useNavigate();
  const [orderList, setOrderList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  const [btnLoader, setBtnloader] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [downloadUrl, setDownloadUrl] = useState("");

  // pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [totalRows, setTotalRows] = useState(0);

  useEffect(() => {
    setLoading(true);
    _getOrderList(currentPage, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentPage, rowsPerPage]);

  useEffect(() => {
    if (downloadUrl.length === 0) return;
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = "";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [downloadUrl]);

  const COLUMNS = [
    {
      name: "Date",
      selector: (row) => row.order_date,
      sortable: true,
      sortFunction: (a, b) => new Date(a.order_date) - new Date(b.order_date),
      width: "150px",
    },
    {
      name: "Username",
      width: "180px",
      selector: (row) => row.name || "-",
    },
    {
      name: "Investment",
      width: "200px",
      selector: (row) => row.investment_name || "-",
    },
    {
      name: "Order ID",
      selector: (row) => row.order_display_id,
      sortable: true,
      width: "180px",
      sortFunction: (a, b) =>
        (a.order_display_id || "")
          .toString()
          .localeCompare((b.order_display_id || "").toString()),
    },
    {
      name: "Investment Type",
      selector: (row) => row.investment_type,
      width: "130px",
      cell: (row) => (
        <Chip
          borderColor="e-border-1px-blue"
          fontColor="e-text-blue-violet"
          fontWeight="e-font-weight-500"
          fontSize="e-font-14"
          label={row.investment_type}
        />
      ),
    },
    {
      name: "Type",
      selector: (row) => row.type || "-",
      width: "100px",
    },
    {
      name: "Amount",
      selector: (row) => row.amount,
      sortable: true,
      sortFunction: (a, b) => (a.amount || 0) - (b.amount || 0),
      cell: (row) => (
        <div className="e-secondary-font-Inter">
          {row.amount_text || `₹${(row.amount || 0).toLocaleString()}`}
        </div>
      ),
      width: "140px",
    },
    {
      name: "Order status",
      width: "130px",
      selector: (row) => row.order_status,
      cell: (row) => {
        switch ((row.order_status || "").toLowerCase()) {
          case "success":
            return (
              <Chip
                fontWeight="e-font-weight-500"
                label="Success"
                borderColor="e-border-1px-green"
                bgColor="e-bg-pale-green"
                fontColor="e-text-green"
              />
            );
          case "failed":
            return (
              <Chip
                fontWeight="e-font-weight-500"
                label="Failed"
                borderColor="e-border-1px-ruby"
                bgColor="e-bg-pale-ruby"
                fontColor="e-text-ruby"
              />
            );
          case "processing":
            return (
              <Chip
                fontWeight="e-font-weight-500"
                label="Processing"
                borderColor="e-border-1px-egyptian-blue"
                bgColor="e-bg-pale-egyptian-blue"
                fontColor="e-text-egyptian-blue"
              />
            );
          default:
            return <span>{row.order_status || "-"}</span>;
        }
      },
    },
    {
      name: "Payment status",
      width: "130px",
      selector: (row) => row.payment_status,
      cell: (row) => {
        switch ((row.payment_status || "").toLowerCase()) {
          case "success":
            return (
              <Chip
                fontWeight="e-font-weight-500"
                label="Success"
                borderColor="e-border-1px-green"
                bgColor="e-bg-pale-green"
                fontColor="e-text-green"
              />
            );
          case "failed":
            return (
              <Chip
                fontWeight="e-font-weight-500"
                label="Failed"
                borderColor="e-border-1px-ruby"
                bgColor="e-bg-pale-ruby"
                fontColor="e-text-ruby"
              />
            );
          case "processing":
            return (
              <Chip
                fontWeight="e-font-weight-500"
                label="Processing"
                borderColor="e-border-1px-egyptian-blue"
                bgColor="e-bg-pale-egyptian-blue"
                fontColor="e-text-egyptian-blue"
              />
            );
          default:
            return <span>{row.payment_status || "-"}</span>;
        }
      },
    },
    {
      name: "Action",
      width: "200px",
      cell: (row) => (
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex justify-content-start align-items-center">
            <PrimaryButton
              name="View"
              onPress={() => {
                navigate(`/orders/detail/${row.order_id}`);
              }}
              ClassName="bg-transparent e-text-blue-violet e-font-weight-400 e-font-14 px-0 py-2"
            />
            <Icon icon="arrow-right2" size="20px" className="ps-1" />
          </div>
        </div>
      ),
    },
  ];

  // service to get orders with pagination
  const _getOrderList = (page = 1, size = 12) => {
    const is_post = true;
    const data = {
      sub_admin_id: id,
      page_num: page,
      page_size: size,
    };
    APIService(is_post, "sub-admin/order-list", data)
      .then((response) => {
        if (response.status_code === 200) {
          const resp = response.data || {};
          setOrderList(resp.orders || []);
          setIsEmpty((resp.orders || []).length === 0);
          setTotalRows(resp.total_count ?? (resp.orders || []).length);
          setRowsPerPage(resp.page_size ?? size);
        } else {
          console.error(response);
          setOrderList([]);
          setTotalRows(0);
          setIsEmpty(true);
        }
      })
      .catch((err) => {
        console.error(err);
        setOrderList([]);
        setTotalRows(0);
        setIsEmpty(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  //service to export Orders
  const _exportOrders = () => {
    setBtnloader(true);
    const is_post = true;
    const data = {
      sub_admin_id: id,
      order_ids: selectedRows.map((r) => r.order_id.toString()),
    };
    APIService(is_post, "sub-admin/order-download", data)
      .then((response) => {
        if (response.status_code === 200) {
          setDownloadUrl(response.data.url);
          toast.success(response.message, {
            type: "success",
            className: "e-toast",
          });
        } else {
          console.error(response);
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setBtnloader(false));
  };

  const handleSelectedRowsChange = ({ selectedRows }) => {
    setSelectedRows(selectedRows);
  };

  if (loading) {
    return (
      <div className="d-flex min-vh-100 justify-content-center align-items-center">
        <Loader width={30} />
      </div>
    );
  }

  if (isEmpty) {
    return <EmptyScreen width={100} message={"No Orders Found"} />;
  }

  return (
    <div className="row px-3 pt-3">
      <div className="col-12">
        <div className="d-flex justify-content-end py-3 ">
          <PrimaryButton
            loading={btnLoader}
            disabled={selectedRows.length === 0}
            name="Download"
            onPress={_exportOrders}
            ClassName="e-bg-blue-violet text-white e-font-14 e-font-weight-500 e-border-radius-pill px-3 py-2"
          />
        </div>
      </div>
      <DataTableComponent
        columns={COLUMNS}
        data={orderList}
        onSelectedRowsChange={handleSelectedRowsChange}
        selectableRows={true}
        sortIcon={<Icon icon="sort_col" size="20px" />}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        paginationPerPage={rowsPerPage}
        currentPage={currentPage}
        onChangePage={(page) => setCurrentPage(page)}
        onChangeRowsPerPage={(newPerPage, page) => {
          setRowsPerPage(newPerPage);
          setCurrentPage(page || 1);
        }}
        className="e-customer-investment-table"
      />
    </div>
  );
}
